import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Warning as WarningIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { authService } from '../../services/authService'

interface MfaDisableDialogProps {
  open: boolean
  onClose: () => void
  onComplete: () => void
}

export const MfaDisableDialog: React.FC<MfaDisableDialogProps> = ({
  open,
  onClose,
  onComplete,
}) => {
  const { t } = useTranslation('profile')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleClose = () => {
    setPassword('')
    setError(null)
    onClose()
  }

  const handleDisable = async () => {
    if (!password) {
      setError('パスワードを入力してください')
      return
    }

    try {
      setLoading(true)
      setError(null)
      await authService.disableMfa(password)
      setPassword('')
      onComplete()
    } catch (err: any) {
      setError(err.response?.data?.error || 'MFAの無効化に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'error.main' }}>
        {t('mfa.disable.title', '2段階認証を無効化')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Alert severity="warning" icon={<WarningIcon />}>
            {t(
              'mfa.disable.warning',
              '2段階認証を無効化すると、アカウントのセキュリティが低下します。本当に無効化しますか？'
            )}
          </Alert>

          {error && <Alert severity="error">{error}</Alert>}

          <Typography variant="body2" color="text.secondary">
            {t(
              'mfa.disable.passwordConfirm',
              '無効化するには、現在のパスワードを入力してください。'
            )}
          </Typography>

          <TextField
            label={t('mfa.disable.password', 'パスワード')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            autoFocus
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel', 'キャンセル')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDisable}
          disabled={loading || !password}
        >
          {loading ? <CircularProgress size={24} /> : t('mfa.disable.confirm', '無効化する')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
